import { cn } from "@gettactic/helpers";
import { mergeRefs } from "@gettactic/helpers/src/dom/mergeRef";
import React from "react";
import { Alert } from "../../data-display/Alert/Alert";
import { Box, Flex } from "../../primitives";
import { FormControlLabel } from "./FormControlLabel";
import { FormControlWrapperContent } from "./FormControlWrapperContent";
import {
  FormControlWrapperContext,
  FormControlWrapperProvider
} from "./FormControlWrapperProvider";
import {
  FormControlWrapperProps,
  disabledCssClasses,
  disabledWrapperClasses,
  formControlWrapperVariants,
  getErrorId,
  getHelperTextId
} from "./defines";

/**
 * The frame shared by the form inputs: label (with required asterisk, optional tag and info
 * tooltip), description, the bordered field with its icons and slots, helper text, character
 * counter, error and success messages. Use it to give a custom control the same look and
 * behaviour as `TextInput`; add `FormControlOutline` inside for the focus / error ring.
 */
export const FormControlWrapper = React.forwardRef<
  HTMLDivElement,
  FormControlWrapperProps
>((props, ref) => {
  const {
    children,
    wrapperClassName,
    className,
    enableSlotsInputFocus = false,
    enableWrapperInputFocus = false,
    hideEmptyMessages = true,
    wrapperRef,
    ...rest
  } = props;
  const containerRef = React.useRef<HTMLDivElement>(null);

  const forwardFocusElement = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    containerRef?.current?.querySelector("input")?.focus();

    return false;
  };

  return (
    <FormControlWrapperProvider {...rest}>
      <FormControlWrapperContext.Consumer>
        {(state) => {
          const charactersCounter = state?.charactersCounter || 0;
          return (
            <Flex
              className={["relative w-full flex-col gap-1", className]}
              ref={mergeRefs([
                ref,
                containerRef,
                ...(wrapperRef ? [wrapperRef] : [])
              ])}
              onClick={(event) => {
                if (
                  enableWrapperInputFocus &&
                  containerRef.current?.contains(event.target as HTMLElement)
                ) {
                  forwardFocusElement(event);
                }
              }}
            >
              {rest.label && (
                <FormControlLabel
                  isRequired={rest.isRequired}
                  isOptional={rest.isOptional}
                  info={rest.info}
                >
                  {rest.label}
                </FormControlLabel>
              )}
              {rest.description && (
                <Box className={"text-content text-md"}>{rest.description}</Box>
              )}
              <Box
                className={cn(
                  rest.variant !== "unstyled" && "py-1",
                  formControlWrapperVariants({ variant: rest.variant }),
                  disabledCssClasses,
                  rest.isDisabled && disabledWrapperClasses,
                  // Read-only values must stay readable: grey background, normal text
                  rest.isReadOnly && "!bg-secondary pointer-events-none",
                  wrapperClassName
                )}
              >
                <FormControlWrapperContent
                  {...rest}
                  enableSlotsInputFocus={enableSlotsInputFocus}
                  slotFocusHandler={(e) => {
                    if (enableSlotsInputFocus) {
                      forwardFocusElement(e);
                    }
                  }}
                >
                  {children}
                </FormControlWrapperContent>
              </Box>
              {rest.showCounter && (
                <Box className={"text-right text-content text-xs"}>
                  {rest.minLength &&
                    (!rest.maxLength ||
                      charactersCounter <= rest.minLength) && (
                      <span>
                        {charactersCounter} of {rest.minLength} characters
                        minimum
                      </span>
                    )}
                  {rest.maxLength &&
                    (!rest.minLength || charactersCounter > rest.minLength) && (
                      <span>
                        {charactersCounter} of {rest.maxLength} characters
                        maximum
                      </span>
                    )}
                </Box>
              )}
              {rest.helperText && (
                <Box
                  id={getHelperTextId(state?.id)}
                  className={"text-content text-xs"}
                >
                  {rest.helperText}
                </Box>
              )}
              {(!hideEmptyMessages || !!rest.error) && (
                <Box
                  id={getErrorId(state?.id)}
                  className={cn(
                    "text-semantic-error-foreground text-xs",
                    !hideEmptyMessages && "min-h-4"
                  )}
                >
                  <Alert
                    variant="error"
                    canDismiss={false}
                    alertTitle={rest.error}
                    alertMessage=""
                    className="px-4 py-2"
                  />
                </Box>
              )}
              {rest.success && (
                <Box
                  className={
                    "bottom-0 mb-[-20px] text-semantic-success-foreground text-xs"
                  }
                >
                  {rest.success}
                </Box>
              )}
            </Flex>
          );
        }}
      </FormControlWrapperContext.Consumer>
    </FormControlWrapperProvider>
  );
});

FormControlWrapper.displayName = "FormControlWrapper";
